<script lang="ts">
    import Background from '$src/lib/components/svg/Background.svelte';
    import FactlinesLogo from '$src/lib/components/svg/FactlinesLogo.svelte';
    import NiceCheck from '$src/lib/components/svg/NiceCheck.svelte';
    import type { PageData } from './$types';
    import { afterNavigate } from '$app/navigation';

    export let data: PageData;

    let top: HTMLElement;

    afterNavigate(() => {
        top.scrollIntoView();
    });

    const company = data.company;
    const id = data.id;
    const token = data.token;
</script>

<svelte:head>
    <meta
        property="og:image"
        content={`https://og-images.factlines.com/api/og?companyName=${company}&token=${token}`}
    />
    <meta
        property="description"
        content={`Factlines bekrefter at ${company} aktivt jobber med grunnleggende menneskerettigheter og anstendige arbeidsforhold i egen virksomhet og leverandørkjede.`}
    />
    <meta
        property="og:description"
        content={`Factlines bekrefter at ${company} aktivt jobber med grunnleggende menneskerettigheter og anstendige arbeidsforhold i egen virksomhet og leverandørkjede.`}
    />
    <meta property="og:title" content="Verifiseringsmerke for åpenhetsloven" />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="600" />
    <meta property="og:url" content={`https://verified.factlines.com/no/${id}`} />
</svelte:head>

<Background />
<span bind:this={top} />
<div id="main" class="relative z-2 flex flex-col w-full min-h-[100vh]">
    <div
        class="inline-flex text-[40px] fixed z-20 flex-col items-start gap-4 bottom-0 left-0 lg:m-8 bg-paper-purple lg:pb-2 pt-2 pr-2 print:hidden"
    >
        <a href="https://factlines.com" class="inline-block text-inherit"><FactlinesLogo /></a>
    </div>
    <main class="">
        <article
            class="verified-article flex flex-col gap-8 my-16 px-[1.75em] w-full max-w-3xl mx-auto print:text-base"
        >
            <NiceCheck />
            <header>
                <h1>Vårt arbeid med Åpenhetsloven</h1>
                <p>
                    <em
                        >Factlines bekrefter at <strong>{data.company}</strong> med
                        organisasjonsnummer <small>(Org.nr: {data.id})</small> aktivt jobber med grunnleggende
                        menneskerettigheter og anstendige arbeidsforhold i egen virksomhet og leverandørkjede.</em
                    >
                </p>
            </header>
            <section class="relative">
                <h2>Kartlegging av leverandører</h2>
                <p>
                    Hvert år samler vi inn informasjon fra våre leverandører gjennom Factlines’
                    programvare. Egenrapporteringen er basert på UN Global Compact og OECDs
                    retningslinjer for ansvarlig næringsliv og er i tråd med prosessen for
                    aktsomhetsvurderinger som kreves i åpenhetsloven.
                </p>
            </section>
            <section class="relative">
                <p>Egenrapporteringen inkluderer følgende temaer:</p>
                <ul class="ml-6">
                    <li>Samfunnsansvar, strategi og retningslinjer</li>
                    <li>Leverandørkjede: oppfølging, innsikt og kontroll</li>
                    <li>Risiko- og aktsomhetsvurderinger</li>
                    <li>Landrisiko</li>
                    <li>Styringssystemer i virksomheten</li>
                    <li>Arbeidsforhold og rettigheter</li>
                    <li>Folkegrupper og miljø</li>
                    <li>Konfliktmineraler</li>
                    <li>Anti-korrupsjon</li>
                </ul>
            </section>
            <section class="relative">
                <p>
                    Datainnsamlingen gir grunnlag for å vurdere om det er etablert god praksis for å
                    overholde åpenhetsloven, lov om offentlige anskaffelser § 5 og virksomhetens
                    egne kontraktsbestemmelser.
                </p>
            </section>
            <section class="relative">
                <p>
                    Kartleggingen gjelder direkteleverandører og produsent/produksjonssted, og
                    henter opplysninger om deres rutiner og prosedyrer for å følge opp hele
                    leverandørkjeden.
                </p>
            </section>
            <section class="relative">
                <p>
                    Informasjon knyttet til risiko i produksjonsland er basert på flere kilder,
                    blant annet ITUC (The International Trade Union Confederation) om
                    arbeidsrettigheter og Transparency International (korrupsjonsindeks).
                </p>
            </section>
            <section class="relative">
                <p>
                    Videre kartlegges forhold rundt arbeidsrettigheter, anti-korrupsjon og hvorvidt
                    leverandørene selv har gjennomført egenrapportering, forbedringsprosjekter
                    og/eller inspeksjoner på produksjonssted. Det blir også kartlagt om
                    leverandørene har etablerte rutiner for blant annet kvalitet, helse og
                    sikkerhet, miljø og samfunnsansvar.
                </p>
            </section>
            <section class="relative">
                <p>
                    Våre leverandører er gjort kjent med våre etiske retningslinjer, og skal levere
                    varer og tjenester som er produsert i samsvar med dem. De er også pålagt å
                    kommunisere og følge opp retningslinjene med sine underleverandører.
                </p>
            </section>
            <section class="relative">
                <p>
                    Dersom egenrapporteringen eller andre indikatorer gir signaler om avvik blir
                    disse fulgt opp med leverandøren det gjelder. Formålet er å kunne forebygge og
                    redusere negative konsekvenser i samarbeid med leverandøren og øvrige aktører i
                    leverandørkjeden.
                </p>
            </section>
            <section class="relative flex items-center gap-4">
                <button
                    class="rounded-full bg-gray bg-opacity-25 w-14 h-14 text-base overflow-hidden"
                    type="button"
                    ><div
                        class="w-full h-full flex items-center justify-center bg-white bg-cover bg-center bg-no-repeat"
                        style="background-image: url(https://cdn.sanity.io/images/s0jfzgdm/production/669f86a296605cef05aaebe32f2e7ca5297c6319-683x1024.png?rect=23,188,544,540&amp;w=120)"
                    /></button
                > <em class="text-lg">Siri Engesæth,<br />CEO</em>
            </section>
            <footer class="relative py-8 border-t">
                <h2>Om Factlines</h2>
                <p>
                    <em
                        >Factlines er et teknologiselskap som utvikler brukervennlig
                        ESG-programvare. Vårt digitale verktøy gjør det enklere å håndtere
                        leverandørrelasjoner og arbeide for en mer rettferdig fremtid, i tråd med
                        viktige europeiske og norske regler.</em
                    >
                </p>
            </footer>
        </article>
    </main>
</div>
